import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  fetchConditionalQuestionnaireSections,
  fetchCoreQuestionnaireSections,
  fetchAssignedQuestionnaireSections,
} from '@/lib/api/questionnaires';
import type { QuestionnaireSummaryCoreResponse } from '@/lib/api/questionnaires';
import { getCurrentUser } from '@/lib/api/auth';
import { useDebounce } from '@/hooks/useDebounce';
import type { QuestionnaireCardData } from '@/components/questionnaires/final-page/types';
import { getCardDescription, getCardTitle } from '@/components/questionnaires/final-page/types';
import { QuestionnaireCard } from '@/components/questionnaires/final-page/QuestionnaireCard';
import { QuestionnaireListRow } from '@/components/questionnaires/final-page/QuestionnaireListRow';
import { QuestionnaireListViewIcon } from '@/components/questionnaires/final-page/QuestionnaireListViewIcon';
import { useQuestionnaireReviewLauncher } from '@/components/questionnaires/review/useQuestionnaireReviewLauncher';
import { getQuestionnaireAssignmentMeta } from '@/components/questionnaires/final-page/meta';
import bookIcon from '@/assets/images/book.svg';
import bulbIcon from '@/assets/images/bulb.svg';

type Filter = 'all' | 'assigned' | 'conditional';
type View = 'grid' | 'list';
type ConditionalGroup = 'all' | 'diabetes' | 'thyroid' | 'insulin-resistance' | 'pcos' | 'other';

const CONDITIONAL_GROUP_OPTIONS: Array<{ id: Exclude<ConditionalGroup, 'other'>; label: string }> = [
  { id: 'all', label: 'All Modules' },
  { id: 'diabetes', label: 'Diabetes' },
  { id: 'thyroid', label: 'Thyroid' },
  { id: 'insulin-resistance', label: 'Insulin Resistance' },
  { id: 'pcos', label: 'PCOS' },
];

const CONDITIONAL_GROUP_LABELS: Record<Exclude<ConditionalGroup, 'all'>, string> = {
  diabetes: 'Diabetes',
  thyroid: 'Thyroid',
  'insulin-resistance': 'Insulin Resistance',
  pcos: 'PCOS',
  other: 'Other',
};

function getConditionalGroup(card: QuestionnaireCardData): Exclude<ConditionalGroup, 'all'> {
  const text = `${getCardTitle(card)} ${getCardDescription(card)}`.toLowerCase();

  if (/\bpcos\b|polycystic/.test(text)) return 'pcos';
  if (/insulin\s*-?\s*resistance/.test(text)) return 'insulin-resistance';
  if (/thyroid|hypothy|hyperthy|hashimoto/.test(text)) return 'thyroid';
  if (/diabetes|\bt1d\b|\bt2d\b/.test(text)) return 'diabetes';

  return 'other';
}

interface CoreQuestionnairesTabProps {
  viewMode: 'grid' | 'list';
  onViewModeChange: (v: 'grid' | 'list') => void;
  workflowFilter: Filter;
  onWorkflowFilterChange: (v: Filter) => void;
  selectedConditionalGroup: Exclude<ConditionalGroup, 'other'>;
  onConditionalGroupChange: (v: Exclude<ConditionalGroup, 'other'>) => void;
  /** Summary from parent (GET /questionnaires/summary?type=core). When provided, metrics and filter counts use it. */
  summary?: QuestionnaireSummaryCoreResponse | null;
}

interface CoreListSectionProps {
  workflowFilter: Filter;
  onWorkflowFilterChange: (v: Filter) => void;
  selectedConditionalGroup: Exclude<ConditionalGroup, 'other'>;
  onConditionalGroupChange: (v: Exclude<ConditionalGroup, 'other'>) => void;
  viewMode: View;
  onViewModeChange: (v: View) => void;
  openDetail: (id: string) => void;
  openAssignment: (id: string) => void;
  openResponses: (id: string) => void;
  openAdditionalQuestions: (card: QuestionnaireCardData) => void;
  pendingQuestionnaireId: string | undefined;
  /** Debounced search term for list queries (from parent). */
  debouncedSearch: string;
}

function CoreQuestionnairesListSection({
  workflowFilter,
  onWorkflowFilterChange,
  selectedConditionalGroup,
  onConditionalGroupChange,
  viewMode,
  onViewModeChange,
  openDetail,
  openAssignment,
  openResponses,
  openAdditionalQuestions,
  pendingQuestionnaireId,
  debouncedSearch,
}: CoreListSectionProps) {
  const user = getCurrentUser();
  const nutritionistId = user?.id;

  const coreQuery = useQuery({
    queryKey: ['questionnaireSections', 'final-page', 'core', nutritionistId, debouncedSearch],
    queryFn: () => fetchCoreQuestionnaireSections(nutritionistId, debouncedSearch || undefined),
  });

  const conditionalQuery = useQuery({
    queryKey: ['questionnaireSections', 'final-page', 'conditional', nutritionistId, debouncedSearch],
    queryFn: () => fetchConditionalQuestionnaireSections(nutritionistId, debouncedSearch || undefined),
  });

  const assignedCoreQuery = useQuery({
    queryKey: ['questionnaireSections', 'final-page', 'assigned', 'core', nutritionistId, debouncedSearch],
    queryFn: () => fetchAssignedQuestionnaireSections('core', nutritionistId, debouncedSearch || undefined),
    enabled: Boolean(nutritionistId) && workflowFilter === 'assigned',
  });

  const coreCards = React.useMemo((): QuestionnaireCardData[] => {
    const sections = coreQuery.data?.questionnaires ?? [];
    return sections.map((s) => ({
      ...s,
      type: 'core',
      status: s.hasDraft ? ('draft' as const) : ('ready' as const),
    }));
  }, [coreQuery.data]);

  const conditionalCards = React.useMemo((): QuestionnaireCardData[] => {
    const sections = conditionalQuery.data?.questionnaires ?? [];
    return sections.map((s) => {
      const templateCount = typeof s.templateQuestionsCount === 'number' && Number.isFinite(s.templateQuestionsCount)
        ? s.templateQuestionsCount
        : 0;
      const questionsCount = typeof s.questionsCount === 'number' && Number.isFinite(s.questionsCount)
        ? s.questionsCount
        : templateCount;
      const assignedClientsCount = typeof s.assignedClientsCount === 'number' && Number.isFinite(s.assignedClientsCount)
        ? s.assignedClientsCount
        : 0;
      return {
        ...s,
        type: 'custom' as const,
        status: 'ready' as const,
        isConditionalModule: true,
        templateQuestionsCount: templateCount,
        questionsCount,
        assignedClientsCount,
      };
    });
  }, [conditionalQuery.data]);

  const assignedCoreCards = React.useMemo((): QuestionnaireCardData[] => {
    const sections = assignedCoreQuery.data?.questionnaires ?? [];
    return sections.map((s) => ({
      ...s,
      type: 'core',
      status: s.hasDraft ? ('draft' as const) : ('ready' as const),
    }));
  }, [assignedCoreQuery.data]);

  const localAssignedCoreCards = React.useMemo(
    () => coreCards.filter((c) => getQuestionnaireAssignmentMeta(c).isAssigned),
    [coreCards]
  );

  const conditionalGroupedCards = React.useMemo(() => {
    const grouped: Record<Exclude<ConditionalGroup, 'all'>, QuestionnaireCardData[]> = {
      diabetes: [],
      thyroid: [],
      'insulin-resistance': [],
      pcos: [],
      other: [],
    };

    conditionalCards.forEach((card) => {
      grouped[getConditionalGroup(card)].push(card);
    });

    const orderedGroups: Array<{ id: Exclude<ConditionalGroup, 'all'>; cards: QuestionnaireCardData[] }> = [
      { id: 'diabetes', cards: grouped.diabetes },
      { id: 'thyroid', cards: grouped.thyroid },
      { id: 'insulin-resistance', cards: grouped['insulin-resistance'] },
      { id: 'pcos', cards: grouped.pcos },
      { id: 'other', cards: grouped.other },
    ];

    return orderedGroups.filter((group) => group.cards.length > 0);
  }, [conditionalCards]);

  const visibleCards = React.useMemo(() => {
    if (workflowFilter === 'conditional') {
      if (selectedConditionalGroup === 'all') {
        return conditionalCards;
      }

      return conditionalCards.filter(
        (card) => getConditionalGroup(card) === selectedConditionalGroup
      );
    }

    if (workflowFilter === 'assigned') {
      if (assignedCoreQuery.data) {
        return assignedCoreCards;
      }
      return localAssignedCoreCards;
    }

    return coreCards;
  }, [
    assignedCoreQuery.data,
    assignedCoreCards,
    conditionalCards,
    coreCards,
    localAssignedCoreCards,
    selectedConditionalGroup,
    workflowFilter,
  ]);

  const renderCards = React.useCallback(
    (cards: QuestionnaireCardData[]) =>
      viewMode === 'grid' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: 2, sm: 3 },
          }}
        >
          {cards.map((card) => (
            <QuestionnaireCard
              key={card.id}
              card={card}
              onPreview={() => openDetail(card.id)}
              onResponses={() => openResponses(card.id)}
              onAssign={() => openAssignment(card.id)}
              showMoreMenu={false}
              preferRawDescription={workflowFilter === 'conditional'}
              variant={workflowFilter === 'conditional' ? 'conditional' : 'default'}
              quickAction={{
                ariaLabel: `Add additional questions to ${getCardTitle(card)}`,
                onClick: () =>
                  openAdditionalQuestions(card, {
                    forceCreateDraft: workflowFilter === 'conditional',
                  }),
                disabled: pendingQuestionnaireId === card.id,
                loading: pendingQuestionnaireId === card.id,
              }}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 2.25 }}>
          {cards.map((card) => (
            <QuestionnaireListRow
              key={card.id}
              card={card}
              onPreview={() => openDetail(card.id)}
              onResponses={() => openResponses(card.id)}
              onAssign={() => openAssignment(card.id)}
              preferRawDescription={workflowFilter === 'conditional'}
              variant={workflowFilter === 'conditional' ? 'conditional' : 'default'}
              leadingIconSrc={workflowFilter === 'conditional' ? bookIcon : undefined}
              quickAction={{
                ariaLabel: `Add additional questions to ${getCardTitle(card)}`,
                onClick: () =>
                  openAdditionalQuestions(card, {
                    forceCreateDraft: workflowFilter === 'conditional',
                  }),
                disabled: pendingQuestionnaireId === card.id,
                loading: pendingQuestionnaireId === card.id,
              }}
            />
          ))}
        </Box>
      ),
    [
      openAdditionalQuestions,
      openAssignment,
      openDetail,
      openResponses,
      pendingQuestionnaireId,
      viewMode,
    ]
  );

  if (coreQuery.isError) {
    return (
      <Paper
        elevation={0}
        sx={{ mt: 3, borderRadius: '18px', border: '1px solid #f3d089', bgcolor: '#fff8e8', px: 2, py: 1.5 }}
      >
        <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#8a5a00' }}>
          Core questionnaire data could not be loaded.
        </Typography>
      </Paper>
    );
  }

  if (coreQuery.isLoading) {
    return (
      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={356} sx={{ borderRadius: '28px' }} />
        ))}
      </Box>
    );
  }

  return (
    <>
      {workflowFilter === 'conditional' && (
          <Box sx={{ mt: 3, display: 'grid', gap: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                minHeight: 76,
                borderRadius: '8px',
                border: '1px solid #4ea7ef',
                bgcolor: 'transparent',
                p: { xs: 1.5, sm: '12px' },
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'flex-start',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 'none',
                  mx: 0,
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1.5, sm: '18px' },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={bulbIcon}
                    alt=""
                    sx={{ width: 20, height: 20, display: 'block' }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '12px',
                      lineHeight: '15px',
                      letterSpacing: '0%',
                      color: '#101828',
                    }}
                  >
                    Conditional Modules
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '145%',
                      letterSpacing: '0%',
                      color: '#667085',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    Follow-up questions that are automatically triggered based on the client&apos;s answers.
                    These modules are added by the system, so practitioners do not need to assign them manually.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 } }}>
              {CONDITIONAL_GROUP_OPTIONS.map((group) => {
                const active = selectedConditionalGroup === group.id;
                return (
                  <Button
                    key={group.id}
                    variant="outlined"
                    onClick={() => onConditionalGroupChange(group.id)}
                    startIcon={
                      <Box
                        component="img"
                        src={bookIcon}
                        alt=""
                        sx={{
                          width: 16,
                          height: 16,
                          display: 'block',
                          opacity: active ? 1 : 0.9,
                          ...(active && { filter: 'brightness(0) invert(1)' }),
                        }}
                      />
                    }
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { xs: '100%', sm: 167 },
                      height: { xs: 44, sm: 36 },
                      minHeight: { xs: 44, sm: 36 },
                      borderRadius: '16777200px',
                      py: { xs: '9px', sm: '7px' },
                      px: { xs: 2, sm: '17px' },
                      gap: '6px',
                      borderWidth: 1,
                      borderColor: active ? '#25a8da' : '#c4ccd5',
                      bgcolor: active ? '#25a8da' : 'transparent',
                      color: active ? '#fff' : '#414b5a',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: 1,
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      boxShadow: 'none',
                      '& .MuiButton-startIcon': {
                        margin: 0,
                      },
                      '&:hover': {
                        borderColor: active ? '#25a8da' : '#b6c0cb',
                        bgcolor: active ? '#1e96c4' : 'transparent',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {group.label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          {workflowFilter === 'conditional' && conditionalQuery.isLoading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: { xs: 2, sm: 3 },
              }}
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={356} sx={{ borderRadius: '28px' }} />
              ))}
            </Box>
          ) : workflowFilter === 'assigned' && assignedCoreQuery.isLoading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: { xs: 2, sm: 3 },
              }}
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={356} sx={{ borderRadius: '28px' }} />
              ))}
            </Box>
          ) : workflowFilter === 'conditional' && selectedConditionalGroup === 'all' && conditionalGroupedCards.length > 0 ? (
            <Box sx={{ display: 'grid', gap: 3 }}>
              {conditionalGroupedCards.map((group) => (
                <Box key={group.id}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '16px', sm: '18px' },
                      color: '#101828',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {CONDITIONAL_GROUP_LABELS[group.id]}
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>{renderCards(group.cards)}</Box>
                </Box>
              ))}
            </Box>
          ) : visibleCards.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: '24px',
                border: '1px dashed #dbe6eb',
                bgcolor: '#fbfdff',
                px: { xs: 2, sm: 3 },
                py: { xs: 4, sm: 7 },
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: { xs: '20px', sm: '24px' }, color: '#0f172a' }}
              >
                {workflowFilter === 'conditional'
                  ? 'No conditional questionnaires'
                  : workflowFilter === 'assigned'
                    ? 'No assigned questionnaires'
                    : 'No core questionnaires'}
              </Typography>
            </Paper>
          ) : (
            renderCards(visibleCards)
          )}
      </Box>
    </>
  );
}

function CoreQuestionnairesTab({
  viewMode,
  onViewModeChange,
  workflowFilter,
  onWorkflowFilterChange,
  selectedConditionalGroup,
  onConditionalGroupChange,
  summary: summaryFromParent,
}: CoreQuestionnairesTabProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openAdditionalQuestions, pendingQuestionnaireId } = useQuestionnaireReviewLauncher();
  const user = getCurrentUser();
  const nutritionistId = user?.id;

  const assignedCoreTotalQuery = useQuery({
    queryKey: ['questionnaireSections', 'final-page', 'assigned', 'core', nutritionistId, ''],
    queryFn: () => fetchAssignedQuestionnaireSections('core', nutritionistId),
    enabled: Boolean(nutritionistId),
  });

  const filterCounts = React.useMemo(() => {
    const s = summaryFromParent;
    const assignedCount = assignedCoreTotalQuery.data?.total ?? 0;
    if (s && s.type === 'core') {
      return {
        all: s.coreQuestionnairesCount ?? s.totalQuestionnaires ?? 0,
        assigned: assignedCount,
        conditional: s.conditionalQuestionnairesCount ?? 0,
      };
    }
    return { all: 0, assigned: assignedCount, conditional: 0 };
  }, [assignedCoreTotalQuery.data?.total, summaryFromParent]);

  const openDetail = React.useCallback(
    (id: string) =>
      navigate(`/questionnaires/${id}`, {
        state: {
          isPreviewMode: true,
          questionnairesBackPath: `${location.pathname}${location.search}`,
        },
      }),
    [location.pathname, location.search, navigate]
  );
  const openAssignment = React.useCallback((id: string) => navigate(`/questionnaires/${id}/assign`), [navigate]);
  const openResponses = React.useCallback((id: string) => navigate(`/questionnaires/${id}/responses`), [navigate]);

  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedSearch = useDebounce(searchQuery.trim(), 300);

  return (
    <Box sx={{ mt: 3, display: 'grid', gap: 3 }}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', lg: 'center' },
            gap: 2,
            borderBottom: { xs: 'none', lg: '1px solid #e2e8f0' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0,
              alignItems: 'center',
              borderBottom: { xs: '1px solid #e2e8f0', lg: 'none' },
              flex: { xs: '0 0 auto', lg: '1 1 auto' },
              minWidth: 0,
            }}
          >
            {([{ key: 'all' as const, label: 'All' }, { key: 'assigned' as const, label: 'Assigned' }, { key: 'conditional' as const, label: 'Conditional' }] as const).map(
              (item) => {
                const isActive = workflowFilter === item.key;
                return (
                  <Button
                    key={item.key}
                    variant="text"
                    onClick={() => onWorkflowFilterChange(item.key)}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: isActive ? '#1699c8' : '#667085',
                      bgcolor: 'transparent',
                      border: 'none',
                      borderBottom: isActive ? '2px solid #1699c8' : '2px solid transparent',
                      py: 1,
                      px: { xs: 1, sm: 1.5 },
                      mr: { xs: 1, sm: 2 },
                      marginBottom: '-1px',
                      borderRadius: 0,
                      minHeight: 44,
                      '&:hover': { bgcolor: 'transparent', color: isActive ? '#1699c8' : '#475467' },
                    }}
                  >
                    <Box component="span" sx={{ mr: 0.75 }}>{item.label}</Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 24,
                        height: 22,
                        px: 1,
                        borderRadius: '999px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '13px', sm: '14px' },
                        bgcolor: isActive ? '#47a9d1' : '#e2e8f0',
                        color: isActive ? '#fff' : '#475467',
                      }}
                    >
                      {filterCounts[item.key]}
                    </Box>
                  </Button>
                );
              }
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              size="small"
              sx={{
                width: { xs: '100%', sm: 180, md: 200 },
                maxWidth: '100%',
                '& .MuiOutlinedInput-root': {
                  height: { xs: 32, sm: 34 },
                  borderRadius: '999px',
                  bgcolor: '#f6f7f9',
                  pl: { xs: 0.25, sm: 0.5 },
                  pr: { xs: 0.375, sm: 0.5 },
                  '& fieldset': { borderColor: '#eaecf0' },
                  '&:hover fieldset': { borderColor: '#dde1e7' },
                  '&.Mui-focused fieldset': { borderColor: '#d5dae2' },
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '12px', sm: '13px' },
                  color: '#8c9199',
                  py: 0,
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#a8acb3',
                  opacity: 1,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: '#b0b3b8', fontSize: { xs: 18, sm: 20 } }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      component="span"
                      sx={{
                        width: { xs: 20, sm: 24 },
                        height: { xs: 18, sm: 22 },
                        borderRadius: { xs: '7px', sm: '8px' },
                        border: '1px solid #d0d2d6',
                        color: '#b4b6bb',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '14px' },
                        lineHeight: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      /
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <ToggleButtonGroup
              exclusive
              value={viewMode === 'list' ? 'list' : ''}
              onChange={(_, v) => onViewModeChange(v === 'list' ? 'list' : 'grid')}
              sx={{
                bgcolor: 'transparent',
                border: 'none',
                p: 0,
                gap: 0.5,
                alignSelf: { xs: 'flex-start', sm: 'center' },
                '& .MuiToggleButtonGroup-grouped': {
                  border: 0,
                  borderRadius: '10px !important',
                  color: '#667085',
                  minWidth: { xs: 40, sm: 36 },
                  height: { xs: 40, sm: 36 },
                },
                '& .Mui-selected': { bgcolor: '#fff !important', color: '#0f172a !important', boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)' },
              }}
            >
              <ToggleButton value="list" aria-label={viewMode === 'list' ? 'Switch to grid view' : 'Switch to list view'}>
                <QuestionnaireListViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <CoreQuestionnairesListSection
          workflowFilter={workflowFilter}
          onWorkflowFilterChange={onWorkflowFilterChange}
          selectedConditionalGroup={selectedConditionalGroup}
          onConditionalGroupChange={onConditionalGroupChange}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          openDetail={openDetail}
          openAssignment={openAssignment}
          openResponses={openResponses}
          openAdditionalQuestions={openAdditionalQuestions}
          pendingQuestionnaireId={pendingQuestionnaireId}
          debouncedSearch={debouncedSearch}
        />
      </Box>
    </Box>
  );
}

export { CoreQuestionnairesTab };
